import { useAppTheme } from "../../hooks/useAppTheme";
import React from "react";
import { View, Text, StyleSheet, TouchableOpacity, Image } from "react-native";
import { FontAwesome } from "@expo/vector-icons";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useDispatch, useSelector } from "react-redux";
import { Avatar } from "react-native-paper";
import * as Auth from "../../services/authService";
import oktaConfig from "../../configs/okta";
import { clearSessionStorage } from "../../services/utils/storageService";
import { persistor } from "../../redux/configureStore";
import { ConfigurationDispatcher } from "../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../redux/constants/actionTypesConfigurations";

type ThemeOption = "light" | "system" | "dark";

const THEME_OPTIONS: { value: ThemeOption; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "system", label: "System" },
  { value: "dark", label: "Dark" },
];

export default function MobileSettingsScreen({ navigation }: any) {
  const { userInfo, profilePhoto, hasAvatar, currentRole, logo, themeMode } =
    useSelector((state: any) => state.configurations);

  const dispatch = useDispatch<any>();
  const { colors } = useAppTheme();

  const displayName =
    userInfo?.display_name && userInfo.display_name !== "undefined"
      ? userInfo.display_name
      : null;
  const fullName: string[] = displayName?.split(" ") ?? [];
  const initial = fullName.length > 0 ? fullName[0].charAt(0) : "U";

  const { loading, notificationCount } = useSelector(
    (state: any) => state.notification,
  );

  const logoutHandler = () => {
    Auth.customLogout().then(() => {
      clearSessionStorage();
      dispatch({ type: "LOGOUT" });
      window.location.href = `${oktaConfig.api.redirectUri}`;
    });
    persistor.purge();
  };

  const isAdmin = ["OWNER", "ADMIN"].includes(currentRole);
  const currentThemeMode: ThemeOption = (themeMode as ThemeOption) ?? "system";

  const setTheme = (value: ThemeOption) => {
    ConfigurationDispatcher(actionTypesConfigurations.ThemeMode, value);
  };

  const menuItems = [
    {
      key: "account",
      icon: <FontAwesome name="user" size={18} color={colors.textSecondary} />,
      label: "Account Settings",
      subtitle: "Profile, password & preferences",
      onPress: () => navigation.navigate("UserSettings", { screen: "Profile" }),
      show: true,
    },
    {
      key: "users",
      icon: <FontAwesome name="users" size={16} color={colors.textSecondary} />,
      label: "User Management",
      subtitle: "Manage team members & roles",
      onPress: () => navigation.navigate("OrgSettings", { screen: "Users" }),
      show: isAdmin,
    },
    {
      key: "org",
      icon: (
        <FontAwesome name="building" size={16} color={colors.textSecondary} />
      ),
      label: "Organisation Settings",
      subtitle: "Company details & configuration",
      onPress: () => navigation.navigate("OrgSettings", { screen: "Company" }),
      show: isAdmin,
    },
    {
      key: "notifications",
      icon: (
        <MaterialCommunityIcons
          name="bell-alert"
          size={20}
          color={colors.textSecondary}
        />
      ),
      label: "Notifications",
      subtitle: "Alerts, reminders & updates",
      onPress: () =>
        navigation.navigate("UserSettings", { screen: "Notification" }),
      show: true,
      badge: !loading && notificationCount > 0 ? notificationCount : null,
    },
  ];

  const styles = createStyles(colors);

  return (
    <View style={styles.container}>
      <View style={{ flex: 1 }}>
        {/* Company Logo */}
        {logo && (
          <View style={styles.logoContainer}>
            <Image source={logo} style={styles.logo} resizeMode="contain" />
          </View>
        )}

        {/* User Profile Card */}
        <View style={styles.profileCard}>
          <View style={styles.avatarContainer}>
            {hasAvatar ? (
              <Avatar.Image
                size={48}
                source={profilePhoto}
                style={{ backgroundColor: colors.surface }}
              />
            ) : (
              <Avatar.Text
                size={48}
                label={initial}
                style={styles.avatarText}
              />
            )}
          </View>
          <View style={styles.profileInfo}>
            <Text style={styles.profileName}>{displayName || "User"}</Text>
            <Text style={styles.profileRole}>
              {currentRole
                ? currentRole.charAt(0) + currentRole.slice(1).toLowerCase()
                : "Member"}
            </Text>
          </View>
        </View>

        {/* Menu Items */}
        <View style={styles.menuSection}>
          {menuItems
            .filter((item) => item.show)
            .map((item, index, arr) => (
              <TouchableOpacity
                key={item.key}
                style={[
                  styles.menuItem,
                  index === arr.length - 1 && { borderBottomWidth: 0 },
                ]}
                onPress={item.onPress}
                activeOpacity={0.6}
              >
                <View style={styles.menuIconContainer}>
                  {item.icon}
                  {item.badge && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>{item.badge}</Text>
                    </View>
                  )}
                </View>
                <View style={styles.menuTextContainer}>
                  <Text style={styles.menuLabel}>{item.label}</Text>
                  <Text style={styles.menuSubtitle}>{item.subtitle}</Text>
                </View>
                <FontAwesome
                  name="chevron-right"
                  size={11}
                  color={colors.border}
                />
              </TouchableOpacity>
            ))}
        </View>

        {/* Appearance */}
        <View style={styles.appearanceSection}>
          <Text style={styles.appearanceTitle}>Appearance</Text>
          <View style={styles.themeSegment}>
            {THEME_OPTIONS.map((opt) => (
              <TouchableOpacity
                key={opt.value}
                style={[
                  styles.themeOption,
                  currentThemeMode === opt.value && styles.themeOptionActive,
                ]}
                onPress={() => setTheme(opt.value)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.themeOptionText,
                    currentThemeMode === opt.value &&
                      styles.themeOptionTextActive,
                  ]}
                >
                  {opt.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Sign Out */}
      <View style={styles.logoutSection}>
        <TouchableOpacity
          style={styles.logoutButton}
          onPress={logoutHandler}
          activeOpacity={0.6}
        >
          <MaterialCommunityIcons
            name="logout"
            size={18}
            color={colors.danger}
          />
          <Text style={styles.logoutText}>Sign Out</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function createStyles(colors: ReturnType<typeof useAppTheme>["colors"]) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    logoContainer: {
      alignItems: "center",
      marginTop: 14,
      marginBottom: 4,
    },
    logo: {
      width: 110,
      height: 30,
    },
    profileCard: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surface,
      marginHorizontal: 14,
      marginTop: 14,
      marginBottom: 8,
      padding: 14,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 1,
    },
    avatarContainer: {
      marginRight: 12,
    },
    avatarText: {
      backgroundColor: "#2563EB",
    },
    profileInfo: {
      flex: 1,
    },
    profileName: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    profileRole: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 2,
    },
    menuSection: {
      backgroundColor: colors.surface,
      marginHorizontal: 14,
      marginTop: 6,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 1,
      overflow: "hidden",
    },
    menuItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 14,
      paddingHorizontal: 14,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
    },
    menuIconContainer: {
      width: 36,
      height: 36,
      borderRadius: 10,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      marginRight: 12,
    },
    menuTextContainer: {
      flex: 1,
    },
    menuLabel: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
    },
    menuSubtitle: {
      fontSize: 12,
      color: colors.textMuted,
      marginTop: 2,
    },
    badge: {
      position: "absolute",
      top: -4,
      right: -4,
      backgroundColor: colors.danger,
      borderRadius: 10,
      paddingHorizontal: 5,
      minWidth: 18,
      height: 18,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 2,
      borderColor: colors.surface,
    },
    badgeText: {
      color: "#fff",
      fontSize: 10,
      fontWeight: "bold",
      textAlign: "center",
    },
    appearanceSection: {
      marginHorizontal: 14,
      marginTop: 12,
      backgroundColor: colors.surface,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      padding: 14,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 1,
    },
    appearanceTitle: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.textSecondary,
      marginBottom: 10,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    themeSegment: {
      flexDirection: "row",
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 8,
      padding: 3,
    },
    themeOption: {
      flex: 1,
      paddingVertical: 8,
      alignItems: "center",
      borderRadius: 6,
    },
    themeOptionActive: {
      backgroundColor: colors.surface,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    themeOptionText: {
      fontSize: 13,
      fontWeight: "500",
      color: colors.textSecondary,
    },
    themeOptionTextActive: {
      color: colors.text,
      fontWeight: "600",
    },
    logoutSection: {
      paddingHorizontal: 14,
      paddingBottom: 24,
    },
    logoutButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 13,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.dangerBorder,
      backgroundColor: colors.dangerBg,
    },
    logoutText: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.danger,
      marginLeft: 8,
    },
  });
}
